/***
 *
 *   ADD CREDITS MODAL
 *   Shared modal body for granting/clawing back credits (CORE-3863). Two
 *   entry points render this: the account Credits tab's "Settle balance"
 *   banner and the user Credits tab's "Add Credits" button.
 *
 **********/

import { useContext, useState } from 'react';
import Axios from 'axios';
import { AuthContext, ViewContext } from 'components/lib';
import { buildWebappUrl, recordWebappAudit } from '../../utility/webapp';
import { computeAddCreditsPreview } from '../../utility/creditPreview';
import { CREDIT_GRANT_CEILING, CREDIT_RETYPE_CONFIRM_THRESHOLD } from '../../utility/creditGuardrails';

const REASON_OPTIONS = [
  { value: 'goodwill', label: 'Goodwill' },
  { value: 'bug', label: 'Bug' },
  { value: 'sales', label: 'Sales' },
  { value: 'correction', label: 'Correction' },
];

const AMOUNT_PRESETS = [100, 200, 500, 1000];

const box = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 20px' };
const label = {
  fontSize: 11,
  fontWeight: 700,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 8,
};
const input = {
  width: '100%',
  padding: '8px 10px',
  fontSize: 14,
  border: '1px solid #e5e7eb',
  borderRadius: 6,
  fontFamily: "'Open Sans', sans-serif",
};

function toggleBtn(active) {
  return {
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 6,
    cursor: 'pointer',
    border: active ? '1px solid #1974f9' : '1px solid #e5e7eb',
    background: active ? '#eff6ff' : '#fff',
    color: active ? '#1974f9' : '#374151',
  };
}

function destinationCard(active) {
  return {
    flex: 1,
    textAlign: 'left',
    padding: '12px 14px',
    borderRadius: 8,
    cursor: 'pointer',
    border: active ? '1px solid #1974f9' : '1px solid #e5e7eb',
    background: active ? '#eff6ff' : '#fff',
  };
}

export function AddCreditsModal(props) {
  const authContext = useContext(AuthContext);
  const context = useContext(ViewContext);

  const [pool, setPool] = useState(props.defaultPool || 'account');
  const [userId, setUserId] = useState(props.defaultUserId || '');
  const [amount, setAmount] = useState(props.defaultAmount != null ? String(props.defaultAmount) : '');
  const [reasonCode, setReasonCode] = useState(props.defaultReasonCode || '');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  // Not security-sensitive — just an idempotency key the server dedupes on
  // per (accountId, requestId) — so Math.random is fine; avoids requiring
  // the Web Crypto API (absent in CRA's default jsdom test environment).
  const [requestId] = useState(() => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`);
  const [confirmStep, setConfirmStep] = useState(false);
  const [retypeValue, setRetypeValue] = useState('');

  const parsedAmount = Number(amount);
  const amountValid = amount !== '' && Number.isInteger(parsedAmount) && parsedAmount !== 0;
  const magnitude = Math.abs(parsedAmount);
  const overCeiling = amountValid && magnitude >= CREDIT_GRANT_CEILING;
  const needsRetypeConfirm = amountValid && !overCeiling && (parsedAmount < 0 || magnitude > CREDIT_RETYPE_CONFIRM_THRESHOLD);
  const selectedUser = props.users.find((u) => u.userId === userId);
  const canSubmit = amountValid && !overCeiling && Boolean(reasonCode) && (pool === 'account' || Boolean(userId)) && !loading;

  const preview = amountValid
    ? computeAddCreditsPreview({
        pool,
        amount: parsedAmount,
        accountDeficit: props.accountDeficit,
        activeLots: props.activeLots,
        personalGranted: selectedUser?.personalGranted ?? 0,
        personalRemaining: selectedUser?.personalRemaining ?? 0,
        personalPeriodEnd: selectedUser?.periodEnd ?? null,
      })
    : null;

  function handleApplyClick(){

    if (needsRetypeConfirm){

      setRetypeValue('');
      setConfirmStep(true);

    }
    else submit();

  }

  async function submit() {
    setLoading(true);
    setErrorMessage(null);

    const body =
      pool === 'personal'
        ? { accountId: props.accountId, amount: parsedAmount, requestId, reasonCode, note, pool: 'personal', userId }
        : { accountId: props.accountId, amount: parsedAmount, requestId, reasonCode, note, pool: 'account' };

    const url = buildWebappUrl(authContext.remote_server, '/api/credit/admin/adjust');
    const auditBase = {
      action: 'credit.adjusted',
      target_type: pool === 'personal' ? 'user' : 'account',
      target_id: pool === 'personal' ? userId : props.accountId,
      account_id: props.accountId,
      method: 'POST',
      endpoint: url,
    };

    try {
      const res = await Axios({ method: 'POST', url, data: body });
      recordWebappAudit({ ...auditBase, status: res.status });
      setLoading(false);
      context.notification.show(
        `${parsedAmount > 0 ? 'Granted' : 'Clawed back'} ${Math.abs(parsedAmount).toLocaleString()} credits`,
        'success',
        true
      );
      context.modal.hide(false, res.data);
    } catch (err) {
      recordWebappAudit({ ...auditBase, status: err.response?.status ?? 0 });
      setLoading(false);
      setErrorMessage(err.response?.data?.error || 'There was a glitch in the matrix – please try again');
    }
  }

  if (confirmStep){

    const targetLabel = pool === 'personal' ? (selectedUser?.email ?? 'this user') : 'the workspace pool';
    const matches = retypeValue.trim() === String(magnitude);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: "'Open Sans', sans-serif" }}>
        <p style={{ fontSize: 13, color: '#374151', margin: 0 }}>
          {parsedAmount < 0
            ? `This claws back ${magnitude.toLocaleString()} credits from ${targetLabel}.`
            : `This grants ${magnitude.toLocaleString()} credits to ${targetLabel}.`}
          {' '}Type {magnitude.toLocaleString()} to confirm.
        </p>
        <input
          aria-label="Type to confirm"
          style={input}
          value={retypeValue}
          onChange={(e) => setRetypeValue(e.target.value)}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" style={toggleBtn(false)} onClick={() => setConfirmStep(false)}>Back</button>
          <button
            type="button"
            disabled={!matches || loading}
            style={{
              padding: '8px 16px', fontSize: 13, fontWeight: 600, borderRadius: 6, border: 'none',
              background: matches ? '#1974f9' : '#e5e7eb', color: matches ? '#fff' : '#9ca3af',
              cursor: matches ? 'pointer' : 'default',
            }}
            onClick={submit}
          >
            {parsedAmount < 0 ? 'Confirm claw-back' : 'Confirm grant'}
          </button>
        </div>
      </div>
    );

  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: "'Open Sans', sans-serif" }}>
      <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
        {props.accountName} · manual ledger entry, no Stripe charge
      </p>

      {/* Destination */}
      <div style={box}>
        <div style={label}>Destination</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={destinationCard(pool === 'account')} onClick={() => setPool('account')}>
            <div style={{ fontSize: 14, fontWeight: 700, color: pool === 'account' ? '#1974f9' : '#111827' }}>
              Workspace pool
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              Shared. Anyone with pool access can spend it.
            </div>
          </button>
          <button type="button" style={destinationCard(pool === 'personal')} onClick={() => setPool('personal')}>
            <div style={{ fontSize: 14, fontWeight: 700, color: pool === 'personal' ? '#1974f9' : '#111827' }}>
              One user's allowance
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              This cycle only. Resets at the next refill.
            </div>
          </button>
        </div>

        {pool === 'personal' && (
          <select
            aria-label="Select user"
            style={{ ...input, marginTop: 8 }}
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          >
            <option value="">Select a user…</option>
            {props.users.map((u) => (
              <option key={u.userId} value={u.userId}>
                {u.email}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Amount */}
      <div style={box}>
        <div style={label}>Amount</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          {AMOUNT_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              style={toggleBtn(amount === String(preset))}
              onClick={() => setAmount(String(preset))}
            >
              {preset.toLocaleString()}
            </button>
          ))}
        </div>
        <input
          aria-label="Amount"
          style={input}
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>
          Whole numbers only. A negative amount claws credits back.
        </p>
        {overCeiling && (
          <p style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>
            Exceeds the {CREDIT_GRANT_CEILING.toLocaleString()} credit limit for a single grant.
          </p>
        )}
      </div>

      {/* Reason */}
      <div style={box}>
        <div style={label}>Reason</div>
        <select aria-label="Reason" style={input} value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}>
          <option value="">Select a reason…</option>
          {REASON_OPTIONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
        <textarea
          aria-label="Note"
          style={{ ...input, marginTop: 8, minHeight: 60 }}
          placeholder="e.g. ticket #4821, deep research failed twice and charged both runs"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      {/* Effect preview */}
      {preview && (
        <div style={{ ...box, background: '#f9fafb' }}>
          <div style={label}>Effect</div>
          <p style={{ fontSize: 13, color: '#374151', margin: 0 }}>{preview.summary}</p>
        </div>
      )}

      {errorMessage && <p style={{ fontSize: 13, color: '#dc2626', margin: 0 }}>{errorMessage}</p>}

      {/* Footer */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between' }}>
        <div style={{ fontSize: 12, color: '#9ca3af' }}>Recorded against {authContext.user?.email}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            style={{ ...toggleBtn(false), whiteSpace: 'nowrap' }}
            onClick={() => context.modal.hide(true)}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 6,
              border: 'none',
              background: canSubmit ? '#1974f9' : '#e5e7eb',
              color: canSubmit ? '#fff' : '#9ca3af',
              cursor: canSubmit ? 'pointer' : 'default',
            }}
            onClick={handleApplyClick}
          >
            Apply to ledger
          </button>
        </div>
      </div>
    </div>
  );
}
